import { t as uiText } from '../i18n';
// src/components/CrawlerPreviewModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import './CrawlerPreviewModal.css';
import DRSView from './DRSView';

interface CrawlerPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardData: any;
}

export const CrawlerPreviewModal: React.FC<CrawlerPreviewModalProps> = ({
  isOpen,
  onClose,
  boardData,
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const [activeTab, setActiveTab] = useState<'data' | 'drs'>('data');

  // open時は常にデータタブに戻す（前回のタブ状態を残さない）
  useEffect(() => {
    if (isOpen) setActiveTab('data');
  }, [isOpen, boardData]);

  const boardId = useMemo(() => {
    const candidate =
      boardData?.id ||
      boardData?.boardId ||
      boardData?.pageId ||
      boardData?.documentId ||
      boardData?.crawlMetadata?.boardId;
    return typeof candidate === 'string' ? candidate.trim() : '';
  }, [boardData]);

  const boardTitle = useMemo(() => {
    const t = boardData?.title || boardData?.editData?.title;
    return typeof t === 'string' ? t : undefined;
  }, [boardData]);

  if (!isOpen || !boardData) return null;

  // コンテンツを抽出（editData.content と previewData の両方をチェック）
  const extractTextContent = (boardData: any): string => {
    // 1. editData.content を優先的にチェック
    if (boardData.editData?.content) {
      return String(boardData.editData.content);
    }
    
    // 2. previewData をチェック
    if (boardData.previewData) {
      const previewData = boardData.previewData;
      
      // contentフィールドがある場合
      if (previewData.content) {
        if (typeof previewData.content === 'string') {
          return previewData.content;
        }
        // JSON形式の場合はJSONをパースして表示
        try {
          const parsed = JSON.parse(previewData.content);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return String(previewData.content);
        }
      }
      
      // textフィールドがある場合
      if (previewData.text) {
        return String(previewData.text);
      }
      
      // bodyフィールドがある場合
      if (previewData.body) {
        return String(previewData.body);
      }
      
      // その他の場合はJSON文字列として表示
      return JSON.stringify(previewData, null, 2);
    }
    
    return '';
  };

  const renderValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null) return <span className="null-value">null</span>;
    if (value === undefined) return <span className="undefined-value">undefined</span>;
    
    if (typeof value === 'string') {
      return <span className="string-value">"{value}"</span>;
    }
    
    if (typeof value === 'number' || typeof value === 'boolean') {
      return <span className="primitive-value">{String(value)}</span>;
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="empty-array">[]</span>;
      if (depth > 3) return <span className="truncated">[...]</span>;
      
      return (
        <div className="array-value">
          <div className="bracket">[</div>
          <div className="array-items">
            {value.map((item, index) => (
              <div key={index} className="array-item">
                <span className="array-index">{index}:</span>
                {renderValue(item, depth + 1)}
              </div>
            ))}
          </div>
          <div className="bracket">]</div>
        </div>
      );
    }
    
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return <span className="empty-object">{'{}'}</span>;
      if (depth > 3) return <span className="truncated">{'{...}'}</span>;
      
      return (
        <div className="object-value">
          <div className="bracket">{'{'}</div>
          <div className="object-items">
            {keys.map((key) => (
              <div key={key} className="object-item">
                <span className="object-key">{key}:</span>
                {renderValue(value[key], depth + 1)}
              </div>
            ))}
          </div>
          <div className="bracket">{'}'}</div>
        </div>
      );
    }
    
    return <span>{String(value)}</span>;
  };

  return (
    <div className="crawler-preview-modal-overlay" onClick={onClose}>
      <div className="crawler-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{uiText("Crawler page data")}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            type="button"
            className={`modal-tab ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => setActiveTab('data')}
          >
            {uiText("Data")}
          </button>
          <button
            type="button"
            className={`modal-tab ${activeTab === 'drs' ? 'active' : ''}`}
            onClick={() => setActiveTab('drs')}
          >
            DRS
          </button>
        </div>
        
        <div className="modal-content">
          {activeTab === 'drs' ? (
            <div className="drs-tab-container">
              {routeWorkspaceId && boardId ? (
                <DRSView
                  workspaceId={routeWorkspaceId}
                  boardId={boardId}
                  title={boardTitle}
                />
              ) : (
                <div className="data-section">
                  <h3>DRS</h3>
                  <div style={{ color: '#666' }}>
                    {uiText("Cannot show DRS because workspaceId / boardId could not be determined.")}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="data-tab-container">
              <div className="data-section">
                <h3>{uiText("Basic information")}</h3>
                <div className="data-item">
                  <span className="data-label">URL:</span>
                  {boardData.crawlMetadata?.sourceUrl ? (
                    <a 
                      href={boardData.crawlMetadata.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {boardData.crawlMetadata.sourceUrl}
                    </a>
                  ) : (
                    <span className="string-value">{uiText("\"Not retrieved\"")}</span>
                  )}
                </div>
                <div className="data-item">
                  <span className="data-label">isCrawled:</span>
                  {renderValue(boardData.isCrawled)}
                </div>
                <div className="data-item">
                  <span className="data-label">title:</span>
                  {renderValue(boardData.title || boardData.editData?.title)}
                </div>
              </div>

              {(boardData.previewData || boardData.editData?.content) && (
                <div className="data-section">
                  <h3>{uiText("Retrieved data")}</h3>
                  <div className="content-display">
                    {extractTextContent(boardData)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

